// ABOUTME: The breathing round's IN/OUT cue: flips on each upward crossing of the canoe swell's midpoint, read from the running animation.
// ABOUTME: With reduced motion there is no animation to read, so the cue alternates on a timer of one cycle length.

import { useEffect, useRef, useState, type RefObject } from 'react';

const POLL_MS = 80;

interface UseBreathCueArgs {
  armed: boolean;
  reducedMotion: boolean;
  swellDur: number; // seconds; the reduced-motion timer's interval
  roundKey: number; // a new round starts the cue on IN again
}

export interface BreathCue {
  cueIn: boolean;
  canoeRef: RefObject<HTMLElement | null>; // the element carrying the swell animation
}

export function useBreathCue({ armed, reducedMotion, swellDur, roundKey }: UseBreathCueArgs): BreathCue {
  // The cue is derived from a flip count that belongs to one round; a new round, or no round, reads as IN again.
  const token = `${armed}:${roundKey}`;
  const [flipped, setFlipped] = useState({ token, count: 0 });
  const cueIn = flipped.token !== token || flipped.count % 2 === 0;
  const flip = () => setFlipped(previous => ({ token, count: (previous.token === token ? previous.count : 0) + 1 }));
  const canoeRef = useRef<HTMLElement | null>(null);
  const swellDurRef = useRef(swellDur);
  useEffect(() => { swellDurRef.current = swellDur; });

  // The flip is once per canoe cycle (each line shows for a full cycle length), as measured on the reference.
  useEffect(() => {
    if (!armed) return;

    if (reducedMotion) {
      let timer: ReturnType<typeof setTimeout>;
      const next = () => {
        timer = setTimeout(() => {
          flip();
          next();
        }, swellDurRef.current * 1000);
      };
      next();
      return () => clearTimeout(timer);
    }

    let previous: number | undefined;
    const id = setInterval(() => {
      const animation = canoeRef.current?.getAnimations?.()[0];
      const progress = animation?.effect?.getComputedTiming().progress;
      if (typeof progress !== 'number') return;
      if (previous !== undefined && previous < 0.5 && progress >= 0.5) flip();
      previous = progress;
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flip is recreated every render and only closes over the token
  }, [armed, reducedMotion, roundKey]);

  return { cueIn, canoeRef };
}
